// ABOUTME: Custom hook for managing infinite scroll states and transitions
// ABOUTME: Handles NORMAL, LOADING, ERROR, and COMPLETE states with proper transitions
import { useCallback, useState } from 'react';

export enum InfiniteScrollState {
    NORMAL = 'NORMAL',
    LOADING = 'LOADING',
    ERROR = 'ERROR',
    COMPLETE = 'COMPLETE',
}

type IUseInfiniteScrollStateReturn = {
    readonly state: InfiniteScrollState;
    readonly errorMessage: string | null;
    readonly isLoading: boolean;
    readonly canLoadMore: boolean;
    readonly startLoading: () => void;
    readonly finishLoading: () => void;
    readonly setError: (message: string) => void;
    readonly setComplete: () => void;
    readonly reset: () => void;
};

export const useInfiniteScrollState = (): IUseInfiniteScrollStateReturn => {
    const [state, setState] = useState<InfiniteScrollState>(InfiniteScrollState.NORMAL);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const startLoading = useCallback(() => {
        setState(InfiniteScrollState.LOADING);
        setErrorMessage(null);
    }, []);

    const finishLoading = useCallback(() => {
        setState(InfiniteScrollState.NORMAL);
        setErrorMessage(null);
    }, []);

    const setError = useCallback((message: string) => {
        setState(InfiniteScrollState.ERROR);
        setErrorMessage(message);
    }, []);

    const setComplete = useCallback(() => {
        setState(InfiniteScrollState.COMPLETE);
        setErrorMessage(null);
    }, []);

    const reset = useCallback(() => {
        setState(InfiniteScrollState.NORMAL);
        setErrorMessage(null);
    }, []);

    // Computed properties
    const isLoading = state === InfiniteScrollState.LOADING;
    const canLoadMore = state === InfiniteScrollState.NORMAL || state === InfiniteScrollState.ERROR;

    return {
        state,
        errorMessage,
        isLoading,
        canLoadMore,
        startLoading,
        finishLoading,
        setError,
        setComplete,
        reset,
    };
};
